import { type ReactElement, type ReactNode } from 'react'
import {
  ListItemIcon,
  ListItemText, // we use the one from material, since we are deviating from our LisItemTex (sx & primary type)
  // reason: this is the wrapped component which we will allow since this is the exported version
  // eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
  MenuItem as MuiMenuItem,
  type MenuItemProps as MuiMenuItemProps,
  type SvgIconProps,
} from '@mui/material'
import { CheckNormalIcon } from '@dhl-ui/icons'
import { type DataTestIdAttribute } from '../../types'
import { Checkbox } from '../../inputs/Checkbox'

type CheckboxValueProps<OptionValue = string, WithCheckbox extends boolean = false> = WithCheckbox extends true
  ? {
      /**
       * the value of the MenuItem, required when WithCheckbox is `true`
       */
      value: OptionValue
      icon?: never
      isDestructive?: never
    }
  : {
      /**
       * the value of the MenuItem, optional
       */
      value?: OptionValue
    }

export type MenuItemProps<OptionValue = string, WithCheckbox extends boolean = false> = Omit<
  MuiMenuItemProps,
  'value' | 'children' | 'sx'
> & {
  /**
   * children, used as display, required
   */
  children: ReactNode

  /**
   * description, add additional text, shown underneath the children
   */
  description?: ReactNode
  /**
   * Destructive show in our 'error' colour
   * @default false
   */
  isDestructive?: boolean
  /**
   * when set, shows a checkbox on the left side, and used `{children}` as the label
   * the `selected` state is used as `checked`
   */
  withCheckbox?: WithCheckbox
  /**
   * Icon to show on the left side
   */
  icon?: ReactElement<SvgIconProps>
  /**
   *  indeterminate state, used when `withCheckbox` is `true`
   */
  indeterminate?: boolean
} & DataTestIdAttribute &
  CheckboxValueProps<OptionValue, WithCheckbox> &
  (
    | {
        isDestructive?: boolean
        indeterminate?: never
        selected?: never // necessary?
        withCheckbox?: never
      }
    | {
        selected?: boolean
        indeterminate?: boolean
        isDestructive?: never
        withCheckbox?: boolean
      }
  )

/**
 * MenuItem
 *
 * Demos:
 * - [MenuItem](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-menuitem--docs)
 *
 */
export const MenuItem = <OptionValue = string, WithCheckbox extends boolean = false>({
  value,
  children,
  icon,
  selected,
  indeterminate,
  disabled,
  isDestructive = false,
  description,
  withCheckbox,
  ...props
}: MenuItemProps<OptionValue, WithCheckbox>) => {
  const label = (
    <ListItemText
      primary={children}
      secondary={description}
      sx={{
        '> .MuiTypography-root': { color: 'inherit' },
        '> .MuiListItemText-primary': {
          fontWeight: 'normal',
          fontFamily: 'inherit',
        },
      }}
    />
  )

  return (
    <MuiMenuItem
      {...props}
      disabled={disabled}
      selected={selected}
      sx={{
        ...(isDestructive ? { color: 'error.main' } : {}),
        ...(disabled ? { color: 'text.disabled' } : {}),
        '> .MuiFormControlLabel-root': { width: '100%' },
        '&.Mui-selected': {
          backgroundColor: 'unset',
        },
      }}
    >
      {!!withCheckbox && (
        <Checkbox value={value} label={label} indeterminate={indeterminate} disabled={disabled} checked={selected} />
      )}
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      {!withCheckbox && label}
      {selected && !isDestructive && !disabled && !withCheckbox && (
        <ListItemIcon sx={{ justifySelf: 'flex-end', color: 'success.main' }}>
          <CheckNormalIcon />
        </ListItemIcon>
      )}
    </MuiMenuItem>
  )
}
